/**
 * AppPromo — the lead-magnet band on every /tools page.
 *
 * The calculators are the same estimating engine that runs inside Antero
 * Trail; this band says so plainly and offers the app. Navy panel in both
 * themes (it is the brand surface, not a token surface), amber CTA.
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

interface AppPromoProps {
  /**
   * One sentence tying THIS calculator to the app, e.g. "In the app, a crane
   * scenario lands in your estimate as priced Xactimate line items."
   */
  context?: string;
  /** Eyebrow text; the index page overrides the singular default. */
  eyebrow?: string;
  className?: string;
}

export default function AppPromo({
  context,
  eyebrow = 'The engine behind this calculator',
  className = '',
}: AppPromoProps) {
  return (
    <section
      aria-label="About the Antero Trail app"
      className={`overflow-hidden rounded-lg bg-[#12294a] text-white ${className}`}
    >
      <div className="relative px-6 py-7 sm:px-8">
        {/* dimension-tick corner mark */}
        <div
          aria-hidden
          className="absolute left-0 top-0 h-full w-1 bg-amber-500"
        />
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-400">
          {eyebrow}
        </p>
        <h2 className="mt-2 font-ui text-2xl font-bold tracking-tight text-white">
          This same math runs inside AI Estimate Assistant
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-200">
          AI Estimate Assistant, from Antero Trail, turns field notes, photos,
          and floor plans into carrier-ready Xactimate estimates. The
          calculator on this page is one piece of that engine, pulled out and
          made free.{' '}
          {context ??
            'In the app, the same scenario lands directly in your estimate as priced line items with the justification attached.'}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-[#12294a] transition-colors hover:bg-amber-400"
          >
            Try AI Estimate Assistant free
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="https://anterotrail.com"
            className="text-sm text-slate-300 underline underline-offset-4 hover:text-white"
          >
            See what the app does
          </a>
        </div>
      </div>
    </section>
  );
}
